import { ModuleGridController } from "./ModuleGridController.js";
import { RARITY_ORDER, type ModuleRarity } from "../modules/ProjectileModule.js";
import type { FootprintId } from "../data/moduleFootprints.js";
import { findPath } from "../modules/ModulePathFinder.js";
import { cellPortsForPlaced } from "../data/modulePorts.js";
import {
  dirDelta,
  DIRECTIONS,
  type Direction,
  type GridPosition,
} from "../models/ModuleGrid.js";

/** 추천 장착 후보 — 미배치 보유 인스턴스의 배치 결정에 필요한 최소 정보(렌더/Phaser 무관). */
export interface EquipCandidate {
  readonly instanceId: string;
  readonly definitionId: string;
  readonly shapeId: string;
  readonly footprintId: FootprintId;
  /** 합성 오버라이드 반영된 유효 등급(effectiveRarity 결과) — 높은 등급 우선 정렬 키. */
  readonly rarity: ModuleRarity;
  /** 동률(같은 등급) 시 결정론 정렬 키 — 먼저 획득한 것 우선. */
  readonly acquiredAt: number;
}

/** 추천 장착 1건 — 어떤 인스턴스를 어느 anchor 칸에 둘지. */
export interface EquipPlacement {
  readonly instanceId: string;
  readonly x: number;
  readonly y: number;
}

/**
 * 추천 장착 계획 (순수) — 보유 미배치 후보 중 **실제 active 경로에서 동작하는 배치만** 높은 등급순으로 채운다.
 * 기존 배치는 유지(빈 칸만 채움). 실 그리드는 변형하지 않고 클론 시뮬에 배치하며 findPath 결과로 검증하고,
 * 그리드에 커밋하는 것은 호출자(GridCommands.recommendEquip) 책임이다.
 *
 * 정렬: RARITY_ORDER 내림차순(transcendent 먼저) → 동률이면 acquiredAt 오름차순(먼저 획득 우선, 결정론).
 * 셀 스캔: row-major(y 외곽, x 내부) 후보 중 배치 후 connected=true, 해당 후보 active=true인 칸만 채택.
 * 이미 active인 기존/추천 모듈을 비활성화하는 배치는 배제한다.
 */
export function planRecommendedEquip(
  grid: ModuleGridController,
  candidates: readonly EquipCandidate[],
): EquipPlacement[] {
  // 실 그리드 비변형 — 클론에 시뮬 배치해 후보 간 점유(footprint)를 반영하며 빈 칸을 찾는다.
  const sim = ModuleGridController.fromState(grid.toState());
  const pending = sortCandidates(candidates);
  const placements: EquipPlacement[] = [];

  const protectedActive = new Set(findPath(sim).activeInstanceIds);
  const pathPlan = planLongestActivePath(sim, pending, protectedActive);
  if (pathPlan.length > 0) {
    for (const p of pathPlan) {
      const c = pending.find((candidate) => candidate.instanceId === p.instanceId);
      if (!c) continue;
      sim.place(p.x, p.y, c.instanceId, c.definitionId, c.shapeId, c.footprintId);
      placements.push(p);
      protectedActive.add(c.instanceId);
      pending.splice(pending.indexOf(c), 1);
    }
  }

  let progressed = true;
  while (progressed) {
    progressed = false;
    for (let i = 0; i < pending.length; i++) {
      const c = pending[i]!;
      const spot = bestActiveFit(sim, c, protectedActive);
      if (!spot) continue; // 현재 그리드에서 active로 만들 수 없음 — 다른 후보 배치 후 재시도
      sim.place(spot.x, spot.y, c.instanceId, c.definitionId, c.shapeId, c.footprintId);
      placements.push({ instanceId: c.instanceId, x: spot.x, y: spot.y });
      protectedActive.add(c.instanceId);
      pending.splice(i, 1);
      progressed = true;
      break; // 낮은 등급 배치 뒤 높은 등급이 열릴 수 있으므로 다시 높은 등급부터 본다.
    }
  }
  return placements;
}

function planLongestActivePath(
  grid: ModuleGridController,
  candidates: readonly EquipCandidate[],
  protectedActive: ReadonlySet<string>,
): EquipPlacement[] {
  const paths = enumerateCellPaths(grid);
  let best: { placements: EquipPlacement[]; rarityScore: number; pathLength: number } | null = null;
  for (const path of paths) {
    const trial = ModuleGridController.fromState(grid.toState());
    const used = new Set<string>();
    const placements: EquipPlacement[] = [];
    let rarityScore = 0;
    let valid = true;

    for (let i = 0; i < path.length; i++) {
      const cell = path[i]!;
      const required = requiredPortsForPathCell(grid, path, i);
      if (!required) {
        valid = false;
        break;
      }
      const cover = trial.moduleCoveringCell(cell.x, cell.y);
      if (cover) {
        const ports = trial.pathPortsAt(cell.x, cell.y);
        if (!hasPorts(ports, required)) {
          valid = false;
          break;
        }
        continue;
      }
      const candidate = candidates.find((c) => {
        if (used.has(c.instanceId)) return false;
        if (!trial.canPlace(cell.x, cell.y, c.footprintId, c.instanceId)) return false;
        if (!hasPorts(cellPortsForPlaced(c.shapeId, c.footprintId)[0] ?? [], required)) return false;
        return true;
      });
      if (!candidate) {
        if (!hasPorts(trial.pathPortsAt(cell.x, cell.y), required)) {
          valid = false;
          break;
        }
        continue;
      }
      trial.place(
        cell.x,
        cell.y,
        candidate.instanceId,
        candidate.definitionId,
        candidate.shapeId,
        candidate.footprintId,
      );
      placements.push({ instanceId: candidate.instanceId, x: cell.x, y: cell.y });
      used.add(candidate.instanceId);
      rarityScore += rarityScoreOf(candidate.rarity);
    }

    if (!valid || placements.length === 0) continue;
    const result = findPath(trial);
    const active = new Set(result.activeInstanceIds);
    if (!placements.every((p) => active.has(p.instanceId))) continue;
    if ([...protectedActive].some((id) => !active.has(id))) continue;

    if (
      !best ||
      placements.length > best.placements.length ||
      (placements.length === best.placements.length && rarityScore > best.rarityScore) ||
      (placements.length === best.placements.length &&
        rarityScore === best.rarityScore &&
        path.length > best.pathLength)
    ) {
      best = { placements, rarityScore, pathLength: path.length };
    }
  }
  return best?.placements ?? [];
}

function enumerateCellPaths(grid: ModuleGridController): GridPosition[][] {
  const start = { x: grid.start.x + 1, y: grid.start.y };
  const end = { x: grid.fire.x - 1, y: grid.fire.y };
  if (!grid.inBounds(start.x, start.y) || !grid.inBounds(end.x, end.y)) return [];
  if (!grid.isUnlocked(start.x, start.y) || !grid.isUnlocked(end.x, end.y)) return [];

  const paths: GridPosition[][] = [];
  const visited = new Set<string>();
  const path: GridPosition[] = [];
  const maxPaths = 50_000;
  const key = (p: GridPosition) => `${p.x},${p.y}`;

  const dfs = (cell: GridPosition): void => {
    if (paths.length >= maxPaths) return;
    visited.add(key(cell));
    path.push(cell);
    if (cell.x === end.x && cell.y === end.y) {
      paths.push(path.map((p) => ({ ...p })));
    } else {
      for (const d of DIRECTIONS) {
        const { dx, dy } = dirDelta(d);
        const next = { x: cell.x + dx, y: cell.y + dy };
        if (!grid.inBounds(next.x, next.y) || !grid.isUnlocked(next.x, next.y)) continue;
        if (visited.has(key(next))) continue;
        dfs(next);
      }
    }
    path.pop();
    visited.delete(key(cell));
  };

  dfs(start);
  return paths;
}

function requiredPortsForPathCell(
  grid: ModuleGridController,
  path: readonly GridPosition[],
  index: number,
): Direction[] | null {
  const cell = path[index]!;
  const prev = index === 0 ? grid.start : path[index - 1]!;
  const next = index === path.length - 1 ? grid.fire : path[index + 1]!;
  const prevDir = directionTo(cell, prev);
  const nextDir = directionTo(cell, next);
  if (!prevDir || !nextDir) return null;
  return prevDir === nextDir ? [prevDir] : [prevDir, nextDir];
}

function directionTo(from: GridPosition, to: GridPosition): Direction | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (dx === 1 && dy === 0) return "right";
  if (dx === -1 && dy === 0) return "left";
  if (dx === 0 && dy === 1) return "down";
  if (dx === 0 && dy === -1) return "up";
  return null;
}

function hasPorts(actual: readonly Direction[], required: readonly Direction[]): boolean {
  return required.every((d) => actual.includes(d));
}

function rarityScoreOf(rarity: ModuleRarity): number {
  return RARITY_ORDER.indexOf(rarity);
}

function sortCandidates(candidates: readonly EquipCandidate[]): EquipCandidate[] {
  return [...candidates].sort((a, b) => {
    const byRarity = RARITY_ORDER.indexOf(b.rarity) - RARITY_ORDER.indexOf(a.rarity);
    if (byRarity !== 0) return byRarity; // 높은 등급 먼저
    return a.acquiredAt - b.acquiredAt; // 동률이면 먼저 획득 우선 (결정론)
  });
}

function bestActiveFit(
  grid: ModuleGridController,
  candidate: EquipCandidate,
  protectedActive: ReadonlySet<string>,
): { x: number; y: number } | null {
  let best: { x: number; y: number; activeCount: number } | null = null;
  for (let y = 0; y < grid.height; y++) {
    for (let x = 0; x < grid.width; x++) {
      const result = evaluateSpot(grid, candidate, protectedActive, x, y);
      if (!result) continue;
      if (!best || result.activeCount > best.activeCount) best = { x, y, ...result };
    }
  }
  return best ? { x: best.x, y: best.y } : null;
}

function evaluateSpot(
  grid: ModuleGridController,
  candidate: EquipCandidate,
  protectedActive: ReadonlySet<string>,
  x: number,
  y: number,
): { activeCount: number } | null {
  const trial = ModuleGridController.fromState(grid.toState());
  if (!trial.place(x, y, candidate.instanceId, candidate.definitionId, candidate.shapeId, candidate.footprintId)) {
    return null;
  }
  const path = findPath(trial);
  const active = new Set(path.activeInstanceIds);
  if (!active.has(candidate.instanceId)) return null;
  for (const id of protectedActive) {
    if (!active.has(id)) return null;
  }
  return { activeCount: path.activeInstanceIds.length };
}
